import { useEffect, useState } from "react"
import type { IdToken } from "@auth0/auth0-react"
import { useAuth0 } from "@auth0/auth0-react"
import jwt_decode from "jwt-decode"

export type TokenData = {
  userToken: string
  connection: string | undefined
  email: string
  partnerVatCode: string
  partnerRole: string
  auth0Role: string
}

const testData: TokenData = {
  userToken: "1234567890098765432asdfghjklpoiuytrewq",
  connection: "connection",
  email: "test@test.it",
  partnerVatCode: "00000000000",
  partnerRole: "owner",
  auth0Role: "User_PiattaformaMediatori"
}

type TokenDataResponse = {
  data: TokenData | null
  accessToken: string | undefined
  error: Error | undefined
  isLoading: boolean
}

export const useTokenData = (): TokenDataResponse => {
  const { getIdTokenClaims, getAccessTokenSilently, error: auth0Error, isLoading } = useAuth0()
  const [userAccessToken, setUserAccessToken] = useState<string>("")
  const [userIdToken, setUserIdToken] = useState<IdToken | undefined>(undefined)
  const [userData, setUserData] = useState<TokenData | null>(null)
  const [claimsError, setClaimsError] = useState<Error | undefined>(undefined)

  useEffect(() => {
    const getToken = async () => {
      const token = await getAccessTokenSilently()
      const claims = await getIdTokenClaims()

      setUserAccessToken(token || "")
      setUserIdToken(claims || undefined)
    }

    if (!isLoading && !auth0Error) {
      getToken()
    }
  }, [isLoading, auth0Error, setUserAccessToken, setUserIdToken, getAccessTokenSilently, getIdTokenClaims])

  useEffect(() => {
    if (userAccessToken && userIdToken) {
      const decodedToken: Record<string, string> = jwt_decode(userAccessToken)
      const claims = decodedToken["https://credimi.com/jwt/claims"]

      if (
        claims === undefined ||
        claims["x-hasura-partner-vat-code"] === undefined ||
        claims["x-hasura-email"] === undefined ||
        claims["x-hasura-default-role"] === undefined
      ) {
        setClaimsError(new Error("Claims not found in token"))
      } else {
        const connection = decodedToken["https://credimi.com/auth0/connection"] ?? null
        const data = {
          userToken: userAccessToken,
          connection,
          email: decodedToken["https://credimi.com/jwt/claims"]["x-hasura-email"],
          partnerVatCode: decodedToken["https://credimi.com/jwt/claims"]["x-hasura-partner-vat-code"],
          partnerRole: decodedToken["https://credimi.com/jwt/claims"]["x-hasura-default-role"],
          auth0Role: decodedToken["https://credimi.com/roles"]
        }
        setUserData(data)
      }
    }
  }, [userAccessToken, userIdToken])

  if (process.env.NODE_ENV === "test") {
    return { data: testData, accessToken: undefined, error: undefined, isLoading: false }
  }

  return { data: userData, accessToken: userAccessToken, error: auth0Error || claimsError, isLoading }
}
